import { IBrokerEntity, TaskDto, TeamDto } from '@maia/shared';
import {
  Button,
  cardStyles,
  FormField,
  Select,
  SpecList,
  TextButton,
  Typography,
} from '@maia/ui';
import { formatDate, getFullName } from '@maia/utils';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

type Props = {
  task: TaskDto;
  brokers: Array<IBrokerEntity>;
  teams: Array<TeamDto>;
  onSubmit: (params: FormOptionsData) => void;
};

export type FormOptionsData = {
  status: string;
  priority: 'in_progress' | 'not_started' | 'closed';
  assignee: string | null;
  team: string;
  publiclyVisible: boolean;
};

export function TaskDetailsSidebar(props: Props) {
  const { task, brokers, teams, onSubmit } = props;
  const { t } = useTranslation('system');
  const form = useForm({
    defaultValues: {
      status: task.status.value,
      priority: task.priority.value as 'in_progress' | 'not_started' | 'closed',
      assignee: task.assignee?.guid ?? null,
      team: '',
      publiclyVisible: false,
    } as FormOptionsData,
    onSubmit: async ({ value }) => onSubmit(value),
  });

  return (
    <div className="flex flex-col gap-y-32">
      <Typography
        variant="display6"
        weight="medium"
        className="text-primaryMain"
      >
        Taakdetails
      </Typography>

      <div className="flex flex-col gap-y-32">
        <div className={twMerge(cardStyles({ variant: 'flat' }), 'p-12')}>
          <SpecList
            items={[
              {
                title: 'Gekoppeld item',
                value: (
                  <TextButton
                    as="link"
                    href={`/${task.linkedItem.type}s/${task.linkedItem.guid}/`}
                    size="tiny"
                  >
                    {t(`linkedItem.${task.linkedItem.type}`)} #
                    {task.linkedItem.label}
                  </TextButton>
                ),
              },
              {
                title: 'Aangemaakt op',
                value: formatDate(task.createdAt),
              },
              {
                title: 'Aangemaakt door',
                value: getFullName(task.createdBy),
              },
              {
                title: 'Verlooptijd',
                value: `${task.leadTime} dagen`,
              },
            ]}
          />
        </div>

        <form
          className="flex flex-col gap-y-24"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.Field name="status">
            {(field) => (
              <FormField
                label="Status"
                inputSlot={
                  <Select
                    placeholder="Selecteren"
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(selection) =>
                      field.handleChange(selection.toString())
                    }
                    items={[
                      {
                        value: 'in_review',
                        label: t('tags.taskStatus.in_review'),
                        meta: {},
                      },
                      {
                        value: 'in_progress',
                        label: t('tags.taskStatus.in_progress'),
                        meta: {},
                      },
                      {
                        value: 'closed',
                        label: t('tags.taskStatus.closed'),
                        meta: {},
                      },
                      {
                        value: 'postponed',
                        label: t('tags.taskStatus.postponed'),
                        meta: {},
                      },
                    ]}
                    className="w-full"
                    enforceMaxHeight
                  />
                }
              />
            )}
          </form.Field>

          <form.Field name="priority">
            {(field) => (
              <FormField
                label="Prioriteit"
                inputSlot={
                  <Select
                    placeholder="Selecteren"
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(selection) =>
                      field.handleChange(selection.toString())
                    }
                    items={[
                      {
                        value: 'low',
                        label: t('tags.taskPriority.low'),
                        meta: {},
                      },
                      {
                        value: 'medium',
                        label: t('tags.taskPriority.medium'),
                        meta: {},
                      },
                      {
                        value: 'high',
                        label: t('tags.taskPriority.high'),
                        meta: {},
                      },
                      {
                        value: 'critical',
                        label: t('tags.taskPriority.critical'),
                        meta: {},
                      },
                    ]}
                    className="w-full"
                    enforceMaxHeight
                  />
                }
              />
            )}
          </form.Field>

          <form.Field name="assignee">
            {(field) => (
              <FormField
                label="Toegewezen aan"
                inputSlot={
                  <Select
                    placeholder="Selecteren"
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(selection) =>
                      field.handleChange(selection.toString())
                    }
                    items={[
                      {
                        value: '',
                        label: 'Niet toegewezen',
                        meta: {},
                      },
                      ...brokers.map((broker) => ({
                        value: broker.guid,
                        label: `${broker.first_name} ${broker.last_name}`,
                        meta: {},
                      })),
                    ]}
                    className="w-full"
                    enforceMaxHeight
                  />
                }
              />
            )}
          </form.Field>

          <form.Field name="team">
            {(field) => (
              <FormField
                label="Team"
                inputSlot={
                  <Select
                    placeholder="Selecteren"
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(selection) =>
                      field.handleChange(selection.toString())
                    }
                    items={teams.map((team) => ({
                      value: team.guid,
                      label: team.name,
                      meta: {},
                    }))}
                    className="w-full"
                    enforceMaxHeight
                  />
                }
              />
            )}
          </form.Field>

          {/* <form.Field name="publiclyVisible">
            {(field) => (
              <FormField
                label="Zichtbaar voor klant"
                labelWeight="medium"
                labelSize="body-s"
                inputSlot={
                  <Select
                    isDisabled
                    placeholder="Selecteren"
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(selection) => field.handleChange(selection)}
                    items={[
                      { value: true, label: 'Ja', meta: {} },
                      { value: false, label: 'Nee', meta: {} },
                    ]}
                    className="w-full"
                    enforceMaxHeight
                  />
                }
              />
            )}
          </form.Field> */}

          <Button type="submit" size="large" intent="cta" className="w-fit">
            Bijwerken
          </Button>
        </form>
      </div>
    </div>
  );
}
